"use client";

import { TabItem, Tabs } from "@marginly/ui/components/tabs";
import cn from "classnames";
import {
  Context,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useSWRConfig } from "swr";

import { saveChartSettings } from "@/actions/chart";
import { chains } from "@/app/config";
import { getTokenName } from "@/app/util";
import { CHART_PERIOD, DATE_FORMAT } from "@/constants/common";
import { useRx } from "@/contexts/rx";
import { useWeb3Context } from "@/contexts/web3";
import { useMarginlyApi } from "@/hooks/api";
import { useDebounce } from "@/hooks/common";
import { ChartPeriod } from "@/types/common";
import { fallbackExtract } from "@/util/api/api";
import { getPriceAggregates, toChartCandleSeries } from "@/util/common";
import { formatNum } from "@/util/format";
import { formatCoin } from "@/util/format-coin";

import Space from "../common/space";
import { TokenImage } from "../common/token";
import CandleChart from "../ui/chart/candle-chart";
import { SelectedCandle } from "../ui/chart/types";
import ClientOnly from "../util/client-only";
import TradeContext from "./context";
import { PendingBlock } from "./styled";
import {
  CandleInfo,
  CandleInfoLabel,
  CandleInfoRow,
  CandleInfoValue,
  ChartContainer,
  GraphSwitcher,
  PressedTotals,
  TabsContainer,
  TradeCoinRow,
} from "./trade.styled";

const TIMEWORDS = {
  H: "past hour",
  "4H": "past 4 hours",
  D: "today",
  W: "past week",
  M: "past month",
  "3M": "past 3 months",
  "6M": "past 6 months",
  Y: "past year",
  All: "all time",
};

type UnwrapContext<T> = T extends Context<infer U> ? U : never;

interface Props
  extends Pick<
      UnwrapContext<typeof TradeContext>,
      "unwrap" | "base" | "quote" | "basePrice"
    >,
    Required<
      Pick<UnwrapContext<typeof TradeContext>, "address" | "uniswapAddress">
    > {
  openPrice?: number;
  liquidationPrice?: number;
  chainName: string;
  internalChainId: number;
  chartSettings: ReturnType<typeof useChartSettings>;
  isPendle?: boolean;
}

export const PriceGraph = ({
  chainName,
  address,
  base: _base,
  basePrice,
  quote: _quote,
  openPrice,
  liquidationPrice,
  internalChainId,
  unwrap,
  uniswapAddress,
  chartSettings,
  isPendle,
}: Props) => {
  const chain = chains[chainName as keyof typeof chains];

  const base: { name: string; symbol: string } | undefined =
    unwrap === "base" ? chain?.nativeCurrency : _base;

  const quote: { name: string; symbol: string } | undefined =
    unwrap === "quote" ? chain?.nativeCurrency : _quote;

  const { setParams } = useRx();

  const [selectedCandle, setSelectedCandle] = useState<
    SelectedCandle | undefined
  >(undefined);

  const chartPeriod = isPendle ? "W" : chartSettings.value.period;
  const chartMode = isPendle ? "line" : chartSettings.value.mode;

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [_width, setWidth] = useState(0);
  const width = useDebounce(_width, { raf: true })[0] || _width;

  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current) {
        const { width } = chartContainerRef.current.getBoundingClientRect();
        setWidth(width);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const onChartTouch = useCallback((candle: SelectedCandle | undefined) => {
    setSelectedCandle(candle);
  }, []);

  const { data: _candles } = useMarginlyApi({
    endpoint: "swapCandles",
    args: [internalChainId, uniswapAddress, chartPeriod],
  });

  const candles = useMemo(
    () => _candles?.points.map(toChartCandleSeries),
    [_candles],
  );

  const price = getPriceAggregates(candles);

  useEffect(() => {
    setParams({ localBgType: price?.up ? "good" : "warn" });

    return () => {
      setParams({ localBgType: undefined });
    };
  }, [price?.up, setParams]);

  return (
    <div>
      <Space height={0} heightMobile={32} />
      {selectedCandle ? (
        <PressedTotals>
          <div style={{ color: selectedCandle.color }}>
            {selectedCandle.diff.lt(0) ? "" : "+"}
            {formatCoin(selectedCandle.diff)} {quote?.symbol} /{" "}
            {formatNum(selectedCandle.diffPercent, {
              prefix: selectedCandle.diff.lt(0) ? "" : "+",
              dp: 2,
            })}
            %
          </div>
          <div className="date">
            {DATE_FORMAT.format(new Date(+selectedCandle.time * 1000))}
          </div>
        </PressedTotals>
      ) : (
        <>
          <TradeCoinRow>
            <TokenImage
              token={base?.symbol}
              rectangle
              className={cn("trade__icon", { "icon--good": price?.up })}
            />
            <ClientOnly>
              <div className="trade__token">
                <div className="token-name">{getTokenName(base)}</div>
                <div className="token-symbol">{base?.symbol}</div>
              </div>
              <div className="trade__cointotals">
                <div>
                  {formatCoin(basePrice)} {quote?.symbol}
                </div>
                <div
                  className={cn("trade__percents", {
                    "trade__percents--good": price?.up,
                  })}
                >
                  {formatNum(price?.percent, {
                    prefix: price && price?.up ? "+" : "",
                    postfix: `% ${TIMEWORDS[chartPeriod]}`,
                    dp: 2,
                  }) ?? "-"}
                </div>
              </div>
            </ClientOnly>
          </TradeCoinRow>
        </>
      )}
      <div className="reverse-container">
        <Space height={32} heightMobile={24} />

        {selectedCandle ? (
          <CandleInfo className="candleinfo">
            <CandleInfoRow>
              <CandleInfoLabel>Open</CandleInfoLabel>
              <CandleInfoLabel>High</CandleInfoLabel>
              <CandleInfoLabel>Low</CandleInfoLabel>
              <CandleInfoLabel>Close</CandleInfoLabel>
            </CandleInfoRow>
            <CandleInfoRow>
              <CandleInfoValue>
                {formatCoin(selectedCandle.open)}
              </CandleInfoValue>
              <CandleInfoValue>
                {formatCoin(selectedCandle.high)}
              </CandleInfoValue>
              <CandleInfoValue>
                {formatCoin(selectedCandle.low)}
              </CandleInfoValue>
              <CandleInfoValue>
                {formatCoin(selectedCandle.close)}
              </CandleInfoValue>
            </CandleInfoRow>
          </CandleInfo>
        ) : (
          <TabsContainer className="tabs-container">
            {isPendle ? null : (
              <Tabs className={cn("tabs", { good: price?.up })}>
                {CHART_PERIOD.map((period) => (
                  <TabItem
                    key={period}
                    className={cn(period === chartPeriod && "active")}
                    onClick={() => chartSettings.set({ period })}
                  >
                    {period}
                  </TabItem>
                ))}
              </Tabs>
            )}

            {isPendle ? null : (
              <GraphSwitcher
                className={cn(
                  { bad: !price?.up },
                  { candles: chartMode === "line" },
                )}
                onClick={() =>
                  chartSettings.set({
                    mode: chartMode === "line" ? "candles" : "line",
                  })
                }
              >
                {chartMode === "line" ? (
                  <img
                    src="/images/gr-candles.svg"
                    alt=""
                    style={{ filter: "invert(var(--svg-invert))" }}
                  />
                ) : (
                  <img
                    src="/images/gr-linear.svg"
                    alt=""
                    style={{ filter: "invert(var(--svg-invert))" }}
                  />
                )}
              </GraphSwitcher>
            )}
          </TabsContainer>
        )}

        <ChartContainer ref={chartContainerRef}>
          <Space height={0} heightMobile={34} className="hide-desktop" />

          <ClientOnly fallback={<div style={{ height: 400 }} />}>
            <CandleChart
              internalChainId={internalChainId}
              uniswapAddress={uniswapAddress}
              openPrice={openPrice}
              liquidationPrice={liquidationPrice}
              width={width}
              height={400}
              chartPeriod={chartPeriod}
              onCrosshairMove={onChartTouch}
              poolAddress={address!}
              viewMode={chartMode}
              isDownTrend={!price?.up}
            />
          </ClientOnly>
        </ChartContainer>
      </div>
    </div>
  );
};

/** @deprecated TODO move out */
export const usePreloadedValues = () => {
  const { internalChainId: _internalChainId } = useWeb3Context();
  const { fallback } = useSWRConfig();

  const internalChainId = useMemo(() => {
    const [fChainSearch] = fallbackExtract(fallback, "chainSearch");
    return fChainSearch?.[1].chainInfo?.chainId ?? (_internalChainId as number);
  }, [fallback, _internalChainId]);

  return { internalChainId };
};

interface ChartSettings {
  mode: "line" | "candles";
  period: ChartPeriod;
}

interface Action {
  type: "set";
  payload: Partial<ChartSettings>;
}

const reducer = (state: ChartSettings, action: Action) => {
  switch (action.type) {
    case "set":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

const parseSettingsString = (settings?: string) => {
  const data: Partial<ChartSettings> = settings ? JSON.parse(settings) : {};
  return { mode: data.mode ?? "line", period: data.period ?? "D" };
};

/** @deprecated TODO move out */
export function useChartSettings(cookie?: string) {
  const [settings, dispatch] = useReducer(reducer, parseSettingsString(cookie));
  const prevSettings = useRef(settings);

  useEffect(() => {
    prevSettings.current = settings;
  }, [settings]);

  const set = useCallback((nextSettings: Partial<ChartSettings>) => {
    dispatch({ type: "set", payload: nextSettings });
    saveChartSettings({ ...prevSettings.current, ...nextSettings });
  }, []);

  return { value: settings, set };
}

export default function TradeGraph({
  chainName,
  _chartSettingsCookie,
  isPendle,
}: {
  chainName: string;
  _chartSettingsCookie?: string;
  isPendle?: boolean;
}) {
  const { address, base, basePrice, quote, uniswapAddress, unwrap } =
    useContext(TradeContext);
  const { internalChainId } = usePreloadedValues();

  const chartSettings = useChartSettings(_chartSettingsCookie);

  // fixme values below should be non-nullable as they are coming from pool config
  if (!address || !uniswapAddress) {
    console.error("Missing required data for TradeGraph");
  }

  return (
    <>
      {quote ? (
        <PriceGraph
          chainName={chainName}
          internalChainId={internalChainId}
          address={address!}
          base={base}
          basePrice={basePrice!}
          quote={quote}
          uniswapAddress={uniswapAddress!}
          unwrap={unwrap}
          chartSettings={chartSettings}
          isPendle={isPendle}
        />
      ) : (
        <PendingBlock />
      )}
    </>
  );
}
